import { useState, useEffect, useRef } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { extractBrandName, getWorkspaceBrandName } from '../lib/brandName';
import { supabase } from '../lib/supabase';
import { deleteWorkspace, planInEffect } from '../lib/workspaces';
import { openCrispChat } from '../lib/crisp';
import { useToast } from './Toast';
import { DeleteModal } from './DeleteModal';
import { getPlanDisplayName, openCustomerPortal } from '../lib/plans';
import { PlanLimits } from './PlanLimits';
import type { Workspace } from '../types/database';

interface WorkspaceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceOverride?: Workspace | null;
}

type ActivePanel = 'team' | 'general';

interface MemberWithProfile {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  created_at: string;
  profiles: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    avatar_url: string | null;
  };
}

interface Invitation {
  id: string;
  email: string;
  role: 'admin' | 'member';
  status: 'pending' | 'accepted' | 'revoked';
  created_at: string;
}

export function WorkspaceSettingsModal({ isOpen, onClose, workspaceOverride }: WorkspaceSettingsModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, viewingAs, workspace: activeWorkspace } = useAuthStore();
  const workspace = workspaceOverride || activeWorkspace;
  const [activePanel, setActivePanel] = useState<ActivePanel>('general');
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // General form state
  const [workspaceName, setWorkspaceName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [generalLoading, setGeneralLoading] = useState(false);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteLoading, setInviteLoading] = useState(false);

  // Revoke/remove state
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeInvitationId, setRevokeInvitationId] = useState<string | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  // Billing state
  const [portalLoading, setPortalLoading] = useState(false);

  // Delete workspace state
  const [showDeleteWorkspaceModal, setShowDeleteWorkspaceModal] = useState(false);
  const [deleteWorkspaceLoading, setDeleteWorkspaceLoading] = useState(false);

  // Ellipsis menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setActivePanel('general');
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Initialize workspace name when modal opens
  useEffect(() => {
    if (isOpen && workspace) {
      setWorkspaceName(workspace.name || '');
      setBrandName(getWorkspaceBrandName(workspace, workspace.brand_url ? extractBrandName(workspace.brand_url) : ''));
    }
  }, [isOpen, workspace]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !showRemoveModal && !showRevokeModal && !showDeleteWorkspaceModal) {
        onClose();
      }
    };
    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible, onClose, showRemoveModal, showRevokeModal, showDeleteWorkspaceModal]);

  // Close ellipsis menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  // Fetch members
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['workspaceMembers', workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_members')
        .select('id, user_id, role, created_at, profiles:user_id(id, first_name, last_name, email, avatar_url)')
        .eq('workspace_id', workspace!.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as unknown as MemberWithProfile[];
    },
    enabled: !!workspace?.id && isVisible,
  });

  // Fetch pending invitations
  const { data: invitations } = useQuery({
    queryKey: ['workspaceInvitations', workspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspace_invitations')
        .select('id, email, role, status, created_at')
        .eq('workspace_id', workspace!.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Invitation[];
    },
    enabled: !!workspace?.id && isVisible,
  });

  const { data: trackedPromptCount = 0 } = useQuery({
    queryKey: ['tracked_prompt_count', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return 0;
      const { count, error } = await supabase
        .from('tracked_prompts')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!workspace?.id && isVisible,
  });

  // Get current user's role
  const currentUserMember = members?.find(m => m.user_id === user?.id);
  const isAdmin = currentUserMember?.role === 'owner' || currentUserMember?.role === 'admin';

  // Team invites are for Paid workspaces; a null limit means unlimited.
  const isPaid = workspace?.state === 'paid';
  const teamMemberLimit = workspace?.team_member_limit ?? null;
  const teamSeatsUsed = (members?.length ?? 0) + (invitations?.length ?? 0);
  const hasReachedTeamLimit = teamMemberLimit !== null && teamSeatsUsed >= teamMemberLimit;

  // Default non-admins to the team panel (only after members have loaded)
  useEffect(() => {
    if (isVisible && !membersLoading && !isAdmin && activePanel === 'general') {
      setActivePanel('team');
    }
  }, [isVisible, membersLoading, isAdmin, activePanel]);

  const handleSaveWorkspaceName = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedWorkspaceName = workspaceName.trim();
    const trimmedBrandName = brandName.trim();
    if (!workspace || !trimmedWorkspaceName || !trimmedBrandName) return;

    setGeneralLoading(true);
    try {
      const { error } = await supabase
        .from('workspaces')
        .update({
          name: trimmedWorkspaceName,
          brand_name: trimmedBrandName,
        })
        .eq('id', workspace.id);

      if (error) throw error;

      useAuthStore.getState().setWorkspace({
        ...workspace,
        name: trimmedWorkspaceName,
        brand_name: trimmedBrandName,
      });
      queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
      toast.success('Workspace settings updated');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update workspace settings');
    } finally {
      setGeneralLoading(false);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !workspace || !inviteEmail.trim()) return;

    if (!isPaid) {
      onClose();
      navigate('/plans');
      return;
    }

    if (hasReachedTeamLimit) {
      toast.error(`Your ${getPlanDisplayName(planInEffect(workspace))} plan allows up to ${teamMemberLimit} team member${teamMemberLimit === 1 ? '' : 's'}.`);
      return;
    }

    // Check if already a member
    const existingMember = members?.find(m => m.profiles.email === inviteEmail.trim());
    if (existingMember) {
      toast.error('This user is already a member of this workspace');
      return;
    }

    // Check if already invited
    const existingInvite = invitations?.find(i => i.email === inviteEmail.trim());
    if (existingInvite) {
      toast.error('This email has already been invited');
      return;
    }

    setInviteLoading(true);
    try {
      const { data: inviteData, error } = await supabase
        .from('workspace_invitations')
        .insert({
          workspace_id: workspace.id,
          email: inviteEmail.trim().toLowerCase(),
          role: inviteRole,
          invited_by: user.id,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Send the invitation email via edge function
      if (inviteData?.id) {
        const { error: emailError } = await supabase.functions.invoke('send-workspace-invite', {
          body: { invitationId: inviteData.id },
        });
        if (emailError) {
          console.warn('Failed to send invitation email:', emailError);
        }
      }

      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('member');
      queryClient.invalidateQueries({ queryKey: ['workspaceInvitations'] });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRevokeInvitation = async () => {
    if (!revokeInvitationId) return;
    setRevokeLoading(true);
    try {
      const { error } = await supabase
        .from('workspace_invitations')
        .update({ status: 'revoked' })
        .eq('id', revokeInvitationId);

      if (error) throw error;
      toast.success('Invitation revoked');
      queryClient.invalidateQueries({ queryKey: ['workspaceInvitations'] });
    } catch {
      toast.error('Failed to revoke invitation');
    } finally {
      setRevokeLoading(false);
      setShowRevokeModal(false);
      setRevokeInvitationId(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!removeMemberId) return;
    setRemoveLoading(true);
    try {
      // Find the member being removed to get their user_id
      const removedMember = members?.find(m => m.id === removeMemberId);

      const { error } = await supabase
        .from('workspace_members')
        .delete()
        .eq('id', removeMemberId);

      if (error) throw error;

      // If the removed user's active workspace is this one, switch them away
      if (removedMember?.user_id && workspace?.id) {
        await supabase
          .from('profiles')
          .update({ workspace_id: null })
          .eq('id', removedMember.user_id)
          .eq('workspace_id', workspace.id);
      }

      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['workspaceMembers'] });
    } catch {
      toast.error('Failed to remove member');
    } finally {
      setRemoveLoading(false);
      setShowRemoveModal(false);
      setRemoveMemberId(null);
    }
  };

  const handlePromoteToAdmin = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ role: 'admin' })
        .eq('id', memberId);

      if (error) throw error;
      toast.success('Member promoted to admin');
      queryClient.invalidateQueries({ queryKey: ['workspaceMembers'] });
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleDemoteToMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('workspace_members')
        .update({ role: 'member' })
        .eq('id', memberId);

      if (error) throw error;
      toast.success('Member role updated');
      queryClient.invalidateQueries({ queryKey: ['workspaceMembers'] });
    } catch {
      toast.error('Failed to update role');
    }
  };

  const handleOpenPortal = async () => {
    if (!user) return;
    setPortalLoading(true);
    try {
      const url = await openCustomerPortal(user.id, `${window.location.origin}/overview`);
      if (url) {
        window.location.href = url;
      } else {
        toast.error('No subscription found. Please subscribe to a plan first.');
      }
    } catch {
      toast.error('Failed to open subscription portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const isOwner = currentUserMember?.role === 'owner' || currentUserMember?.role === 'admin';
  const isSoleMember = members?.length === 1;

  const handleDeleteWorkspace = async () => {
    if (!workspace || !user) return;

    if (!isSoleMember) {
      toast.error('You must remove all other members of the workspace before deletion');
      setShowDeleteWorkspaceModal(false);
      return;
    }

    setDeleteWorkspaceLoading(true);
    try {
      const nextWorkspace = await deleteWorkspace(user.id, workspace.id);

      toast.success('Workspace deleted');

      // Refresh auth store and workspace list
      await useAuthStore.getState().fetchProfile(user.id);
      queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });

      onClose();

      if (nextWorkspace?.id) {
        useAuthStore.getState().setWorkspace(nextWorkspace);
        await useAuthStore.getState().fetchProfile(user.id);
        queryClient.invalidateQueries({ queryKey: ['userWorkspaces'] });
        navigate('/overview');
      } else {
        await useAuthStore.getState().fetchProfile(user.id);
        navigate('/onboarding/step-1');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete workspace');
    } finally {
      setDeleteWorkspaceLoading(false);
      setShowDeleteWorkspaceModal(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      default: return 'Member';
    }
  };

  const getMemberName = (member: MemberWithProfile) => {
    const first_name = member.profiles?.first_name;
    const last_name = member.profiles?.last_name;
    if (first_name && last_name) return `${first_name} ${last_name}`;
    if (first_name) return first_name;
    return member.profiles?.email || 'Unknown';
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className={`account-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${!isAnimating && isVisible ? 'is-closing' : ''}`}
      >
        <div className="settings-background" ref={modalRef}>
          <div className="modal-close is-dark-btn" onClick={onClose}>
            <div className="rotated-text">+</div>
          </div>

          <div className="settings-sidebar">
            {isAdmin && (
              <a
                href="#"
                className={`sidebar-link w-inline-block ${activePanel === 'general' ? 'is-active' : ''}`}
                onClick={(e) => { e.preventDefault(); setActivePanel('general'); }}
              >
                <div>General Settings</div>
              </a>
            )}
            <a
              href="#"
              className={`sidebar-link w-inline-block ${activePanel === 'team' ? 'is-active' : ''}`}
              onClick={(e) => { e.preventDefault(); setActivePanel('team'); }}
            >
              <div>Team Management</div>
            </a>
            {isAdmin && isPaid && !viewingAs && (
              <a
                href="#"
                className={`sidebar-link w-inline-block ${portalLoading ? 'is-loading' : ''}`}
                onClick={(e) => { e.preventDefault(); handleOpenPortal(); }}
                style={{ opacity: portalLoading ? 0.6 : 1, pointerEvents: portalLoading ? 'none' : 'auto' }}
              >
                <div>{portalLoading ? 'Loading...' : 'Subscription'}</div>
              </a>
            )}

            <div className="bottom-link-wrapper">
              <PlanLimits workspace={workspace} promptUsed={trackedPromptCount} onUpgrade={onClose} />
            </div>
          </div>

          {/* Team Panel */}
          <div
            className="account-settings-panel"
            style={{ display: activePanel === 'team' ? 'block' : 'none' }}
          >
            <div className="text-size-medium text-weight-semibold">Team</div>
            <div className="spacer-small"></div>

            <div className="text-size-small text-weight-semibold text-colour-alt-grey">
              Members ({(members?.length ?? 0) + (invitations?.length ?? 0)})
            </div>
            <div className="spacer-tiny"></div>

            {/* Members & Invitations Table */}
            <div className="team-table-wrapper">
              <table className="team-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    {isAdmin && <th style={{ width: '48px' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {members?.map((member) => (
                    <tr key={member.id}>
                      <td>
                        <div className="text-weight-medium">
                          {getMemberName(member)}
                          {member.user_id === user?.id && (
                            <span className="text-colour-alt-grey"> (you)</span>
                          )}
                        </div>
                        <div className="text-size-tiny text-colour-alt-grey">{member.profiles?.email}</div>
                      </td>
                      <td>
                        <span className={`role-pill ${member.role === 'owner' ? 'is-owner' : member.role === 'admin' ? 'is-admin' : ''}`}>
                          {getRoleBadge(member.role)}
                        </span>
                      </td>
                      <td>
                        <span className="status-pill is-active">Active</span>
                      </td>
                      {isAdmin && (
                        <td>
                          {member.user_id !== user?.id && member.role !== 'owner' && (
                            <div className="table-actions-wrapper" ref={openMenuId === member.id ? menuRef : undefined}>
                              <button
                                className="table-actions-btn"
                                onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                              >
                                <MoreHorizontal size={16} />
                              </button>
                              {openMenuId === member.id && (
                                <div className="table-actions-menu">
                                  {member.role === 'member' ? (
                                    <button
                                      className="table-actions-menu-item"
                                      onClick={() => { handlePromoteToAdmin(member.id); setOpenMenuId(null); }}
                                    >
                                      Make admin
                                    </button>
                                  ) : (
                                    <button
                                      className="table-actions-menu-item"
                                      onClick={() => { handleDemoteToMember(member.id); setOpenMenuId(null); }}
                                    >
                                      Make member
                                    </button>
                                  )}
                                  <button
                                    className="table-actions-menu-item is-danger"
                                    onClick={() => {
                                      setRemoveMemberId(member.id);
                                      setShowRemoveModal(true);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    Remove user
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {invitations?.map((invitation) => (
                    <tr key={invitation.id} className="is-pending">
                      <td>
                        <div className="text-colour-alt-grey">—</div>
                        <div className="text-size-tiny text-colour-alt-grey">{invitation.email}</div>
                      </td>
                      <td>
                        <span className={`role-pill ${invitation.role === 'admin' ? 'is-admin' : ''}`}>
                          {invitation.role === 'admin' ? 'Admin' : 'Member'}
                        </span>
                      </td>
                      <td>
                        <span className="status-pill is-pending">Pending</span>
                      </td>
                      {isAdmin && (
                        <td>
                          <div className="table-actions-wrapper" ref={openMenuId === invitation.id ? menuRef : undefined}>
                            <button
                              className="table-actions-btn"
                              onClick={() => setOpenMenuId(openMenuId === invitation.id ? null : invitation.id)}
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {openMenuId === invitation.id && (
                              <div className="table-actions-menu">
                                <button
                                  className="table-actions-menu-item is-danger"
                                  onClick={() => {
                                    setRevokeInvitationId(invitation.id);
                                    setShowRevokeModal(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  Revoke invite
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!members || members.length === 0) && (!invitations || invitations.length === 0) && (
                    <tr>
                      <td colSpan={isAdmin ? 4 : 3} style={{ textAlign: 'center', padding: '2rem 0.75rem' }} className="text-colour-alt-grey">
                        No team members yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Invite User (inline below table, admin only) */}
            {isAdmin && (
              <>
                <div className="spacer-small"></div>
                <div className="line-divider is-horizontal"></div>
                <div className="spacer-small"></div>
                <div className="text-size-small text-weight-semibold">Invite user</div>
                <div className="spacer-xtiny"></div>
                <div className="text-size-tiny text-colour-alt-grey">
                  {!isPaid
                    ? 'Team invites are available on the Starter plan and above.'
                    : teamMemberLimit !== null
                      ? `${teamSeatsUsed} of ${teamMemberLimit} team members used`
                      : `${teamSeatsUsed} team members`}
                </div>
                <div className="spacer-xsmall"></div>
                {!isPaid ? (
                  <button
                    type="button"
                    className="button is-small w-button"
                    onClick={() => {
                      onClose();
                      navigate('/plans');
                    }}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Upgrade to invite
                  </button>
                ) : (
                  <form onSubmit={handleInvite} className="invite-inline-form">
                    <div className="invite-inline-top">
                      <input
                        className="form_input w-input"
                        type="email"
                        placeholder="user@email.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        required
                        disabled={hasReachedTeamLimit || inviteLoading}
                        style={{ flex: 1 }}
                      />
                      <div className="invite-role-wrapper">
                        <select
                          className="invite-role-select"
                          value={inviteRole}
                          onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                          disabled={hasReachedTeamLimit || inviteLoading}
                        >
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                    </div>
                    <div className="invite-inline-bottom">
                      <div className="text-size-tiny text-colour-alt-grey invite-role-description">
                        <span className="text-weight-semibold">{inviteRole === 'admin' ? 'Admin' : 'Member'}</span>{' '}
                        {inviteRole === 'admin'
                          ? 'has access to analytics, team management, workspace settings and billing.'
                          : 'can access all analytics but cannot manage billing or team settings.'}
                      </div>
                      <button
                        type="submit"
                        className="button is-small w-button"
                        disabled={inviteLoading || hasReachedTeamLimit}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {inviteLoading ? 'Sending...' : hasReachedTeamLimit ? 'Limit reached' : 'Send invite'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>

          {/* General Panel */}
          <div
            className="account-settings-panel"
            style={{ display: activePanel === 'general' ? 'flex' : 'none', flexDirection: 'column' }}
          >
            <div>
              <div className="text-size-medium text-weight-semibold">General</div>
              <div className="spacer-small"></div>
              <div className="account-block w-form">
                <form onSubmit={handleSaveWorkspaceName}>
                  <label className="form_label">Workspace name</label>
                  <input
                    className="form_input w-input"
                    type="text"
                    placeholder="My workspace"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    required
                  />
                  <div className="spacer-small"></div>
                  <label className="form_label">Brand name</label>
                  <input
                    className="form_input w-input"
                    type="text"
                    placeholder={workspace?.brand_url ? extractBrandName(workspace.brand_url) : 'Your brand'}
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    required
                  />
                  <div className="spacer-xsmall"></div>
                  <div className="text-size-tiny text-colour-alt-grey">
                    This controls how your brand appears across Lima.
                  </div>
                  <div className="spacer-small"></div>
                  <input
                    type="submit"
                    className="button is-small w-button"
                    value={generalLoading ? 'Saving...' : 'Save changes'}
                    disabled={
                      generalLoading
                      || (
                        workspaceName.trim() === (workspace?.name || '')
                        && brandName.trim() === getWorkspaceBrandName(workspace, workspace?.brand_url ? extractBrandName(workspace.brand_url) : '')
                      )
                    }
                  />
                </form>
              </div>

              <div className="spacer-small"></div>
              <div className="line-divider is-horizontal"></div>
              <div className="spacer-small"></div>

              <div className="account-block w-form">
                <label className="form_label">Brand URL</label>
                <input
                  className="form_input w-input"
                  type="text"
                  value={workspace?.brand_url || ''}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
                <div className="spacer-xsmall"></div>
                <div className="text-size-tiny text-colour-alt-grey">
                  Your brand URL is fixed once the workspace is set up, so its history always belongs to one brand. Contact support if you need to change it.
                </div>
              </div>
            </div>

            <div className="delete-wrapper" style={{ position: 'static', marginTop: '1rem', marginRight: 0, gap: '1.5rem' }}>
              <div
                className="delete-link is-dark"
                onClick={() => {
                  onClose();
                  openCrispChat();
                }}
              >
                Contact support
              </div>
              {isOwner && !viewingAs && (
                <div
                  className="delete-link"
                  onClick={() => {
                    if (!isSoleMember) {
                      toast.error('You must remove all other members of the workspace before deletion');
                      return;
                    }
                    setShowDeleteWorkspaceModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  Delete workspace
                </div>
              )}
            </div>
          </div>

        </div>

        <div className="close-layer" onClick={onClose}></div>
      </div>

      <DeleteModal
        isOpen={showRemoveModal}
        onClose={() => { setShowRemoveModal(false); setRemoveMemberId(null); }}
        onConfirm={handleRemoveMember}
        title="Remove member?"
        message="This person will lose access to this workspace and all its content."
        confirmText="Yes, remove"
        loading={removeLoading}
      />

      <DeleteModal
        isOpen={showRevokeModal}
        onClose={() => { setShowRevokeModal(false); setRevokeInvitationId(null); }}
        onConfirm={handleRevokeInvitation}
        title="Revoke invitation?"
        message="This invitation will be cancelled and the user will not be able to join."
        confirmText="Yes, revoke"
        loading={revokeLoading}
      />

      <DeleteModal
        isOpen={showDeleteWorkspaceModal}
        onClose={() => { setShowDeleteWorkspaceModal(false); }}
        onConfirm={handleDeleteWorkspace}
        title="Delete workspace?"
        message="This action is permanent. All workspace data, including team members, invitations, and subscription, will be permanently deleted."
        confirmText="Yes, delete workspace"
        loading={deleteWorkspaceLoading}
      />
    </>
  );
}
